/**
 * Thin axios wrappers for the Mail List Builder (Outreach MVP).
 *
 * Two endpoints, both POST-ing the same `AudienceSpec`:
 *
 *   POST /outreach/mail-list/preview  — tiered household/voter/cost counts.
 *       Read-gated to {analyst, admin, org_manager, manager, sms_manager}.
 *   POST /outreach/mail-list/export   — the same spec PLUS a `tier`, returns a
 *       text/csv StreamingResponse. Bulk-export is gated tighter server-side to
 *       {admin, org_manager, manager}; the UI mirrors that (analyst may preview
 *       but not export).
 *
 * All requests go through the shared `client` (base URL + Authorization header
 * injection live there). Tenant scope is enforced server-side by the JWT
 * middleware. Pattern mirrors `src/api/candidates.ts` / `src/api/exports.ts`.
 *
 * The candidate multi-select reuses `listCandidates` from `@/api/candidates`;
 * it is re-exported here so a call site only needs one import.
 */
import client from '@/api/client';
import { listCandidates } from '@/api/candidates';

// Re-export so a mail-list call site can pull the candidate list from one place.
export { listCandidates };

// ---------------------------------------------------------------------------
// Request shape — AudienceSpec
// ---------------------------------------------------------------------------

/** Geographic scope. Any subset of the three axes may be supplied. */
export interface AudienceGeo {
  precincts?: string[];
  subregions?: string[];
  legislative_districts?: string[];
}

/** A k-of-n turnout floor: voted in at least `k` of the last `n` elections. */
export interface TurnoutMin {
  k: number;
  n: number;
}

/**
 * Tier score cutoffs (precinct r_lean × turnout propensity). Provisional —
 * defaults {t1: 0.55, t2: 0.40, t3: 0.25}.
 */
export interface TierThresholds {
  t1: number;
  t2: number;
  t3: number;
}

/**
 * The audience definition sent to both preview and export.
 *
 * Contest scope is either candidate-driven (`candidate_ids`) or geo-driven
 * (`geo`): when `candidate_ids` is empty/omitted the backend falls back to the
 * `geo` block. `party_exclude` defaults to `["D"]`; `party_include: null` means
 * "everyone not excluded".
 */
export interface AudienceSpec {
  candidate_ids?: number[] | null;
  geo?: AudienceGeo | null;
  party_exclude?: string[];
  party_include?: string[] | null;
  election_date: string;
  election_type: string;
  turnout_min?: TurnoutMin | null;
  tier_thresholds?: TierThresholds;
  per_piece_rate_cents?: number;
  disclaimer?: string | null;
}

/** Which cumulative tier bundle to export. */
export type MailListTier = 't1' | 't1_2' | 't1_2_3';

// ---------------------------------------------------------------------------
// Filter options — GET /outreach/options
// ---------------------------------------------------------------------------

/** A turf/subregion choice: `key` is the value sent in `geo.subregions`. */
export interface MailListSubregionOption {
  key: string;
  label: string;
}

/**
 * The distinct filter values available for the current tenant, used to feed
 * the audience-builder dropdowns. Arrays arrive already sorted from the
 * backend. When this fetch fails the view falls back to free-text inputs, so
 * the shape is intentionally flat and forgiving.
 */
export interface MailListOptions {
  election_types: string[];
  precincts: string[];
  subregions: MailListSubregionOption[];
  legislative_districts: string[];
  party_codes: string[];
}

// ---------------------------------------------------------------------------
// Response shape — MailListPreview
// ---------------------------------------------------------------------------

/** Counts + cost for one tier slice. */
export interface TierCount {
  households: number;
  voters: number;
  cost_cents: number;
}

/** Household count + cost for a cumulative tier bundle (no `voters`). */
export interface CumulativeCount {
  households: number;
  cost_cents: number;
}

/** The three cumulative bundles: T1, T1+2, T1+2+3. */
export interface CumulativeCounts {
  t1: CumulativeCount;
  t1_2: CumulativeCount;
  t1_2_3: CumulativeCount;
}

/** One contest row (a candidate reach, or the single "geo" bucket). */
export interface ContestPreview {
  key: string;
  label: string;
  t1: TierCount;
  t2_add: TierCount;
  t3_add: TierCount;
  cumulative: CumulativeCounts;
}

/** The totals row: same shape as a contest minus `key`/`label`. */
export interface TotalsPreview {
  t1: TierCount;
  t2_add: TierCount;
  t3_add: TierCount;
  cumulative: CumulativeCounts;
}

/** Full preview response. */
export interface MailListPreview {
  per_piece_rate_cents: number;
  tier_thresholds: TierThresholds;
  contests: ContestPreview[];
  totals: TotalsPreview;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/**
 * Fetch the distinct filter values (election types, precincts, subregions,
 * legislative districts, party codes) for the audience-builder dropdowns.
 * Read-gated the same as preview. Arrays come back pre-sorted.
 */
export async function listMailListOptions(): Promise<MailListOptions> {
  const res = await client.get<MailListOptions>('/outreach/options');
  return res.data;
}

/**
 * Preview the tiered mail audience. Read-gated to
 * {analyst, admin, org_manager, manager, sms_manager}.
 */
export async function previewMailList(spec: AudienceSpec): Promise<MailListPreview> {
  const res = await client.post<MailListPreview>('/outreach/mail-list/preview', spec);
  return res.data;
}

/**
 * Export the mail audience for a cumulative `tier` as a CSV blob. Bulk-export
 * is gated to {admin, org_manager, manager} server-side. Returns a Blob so the
 * caller can trigger a browser download (see `triggerBrowserDownload`).
 */
export async function exportMailList(spec: AudienceSpec, tier: MailListTier): Promise<Blob> {
  const res = await client.post('/outreach/mail-list/export', { ...spec, tier }, {
    responseType: 'blob',
  });
  return res.data;
}
